import { useCallback } from "react";
import * as Sentry from "@sentry/react";

export interface UseLoginCodeReturn {
  verifyLoginCode: (email: string, enteredCode: string) => Promise<unknown>;
}

export const useLoginCode = (): UseLoginCodeReturn => {
  const verifyLoginCode = useCallback(
    async (email: string, enteredCode: string) => {
      try {
        // Call Cloud Function to verify code and get Firebase Auth custom token
        const { getFunctions, httpsCallable } =
          await import("firebase/functions");
        const { app } = await import("../../../config/firebase");
        const { signInWithCustomToken } = await import("firebase/auth");
        const { auth } = await import("../../../config/firebase");

        const functions = getFunctions(app, "europe-west1");
        const verifyCode = httpsCallable(functions, "verifyLoginCode");


        // Call the Cloud Function
        const result = await verifyCode({
          email: email.toLowerCase().trim(),
          code: parseInt(enteredCode.trim()),
        });

        const { customToken, uid } = result.data as {
          customToken?: string;
          uid?: string;
        };

        if (!customToken) {
          throw new Error("Failed to get authentication token");
        }

        // Sign in with the custom token to establish Firebase Auth session
        const userCredential = await signInWithCustomToken(auth, customToken);

        console.log(
          "✅ Login code verified and user authenticated:",
          userCredential.user.uid
        );

        Sentry.logger.info(
          "🚪 Login: Code verified and user authenticated" + userCredential.user.uid,
          {
            log_source: "sentry_test",
          }
        );


        return {
          success: true,
          valid: true,
          uid: uid || userCredential.user.uid,
          firebaseUser: userCredential.user,
        };
      } catch (error: unknown) {
        console.error("❌ Error verifying login code:", error);

        // Handle Firebase Functions errors
        const err = error as { code?: string; message?: string };
        if (err.code) {
          // Use the server's error message if available, otherwise fall back to default messages
          const errorMessages: Record<string, string> = {
            "not-found":
              "No login code found for this email. Please request a new code.",
            "deadline-exceeded":
              err.message ||
              "Login code has expired. Please request a new code from now.",
            "already-exists":
              "This login code has already been used. Please request a new code.",
            "permission-denied":
              "Invalid code. Please check the number in your email and try again.",
            "invalid-argument":
              err.message || "Invalid email or code provided.",
          };

          throw new Error(
            errorMessages[err.code] ||
              err.message ||
              "Failed to verify login code"
          );
        }

        throw error;
      }
    },
    []
  );

  return { verifyLoginCode };
};
